// submitRetry.ts — loop submit + retry dengan klasifikasi error deterministik
import { logError } from '../../../../utils/errorLogger';
import { buildOrderPayload } from './submitPayload';
import { submitPublicOrder } from './submitRpc';
import { handleDuplicateKey } from './submitDuplicate';

export interface SubmitRetryArgs {
  s: any; c: any;
  builtParams: {
    form: any; customerData: any; addressData: any; selectedOptions: any; selectedShippingKey: any;
    selectedPaymentKey: any; selectedDuitkuMethod: any; selectedEverproRate: any; selectedBiteshipRate: any;
    currentCombination: any; linkedProductName: any; quantity: any; shippingCost: any; codFee: any;
    orderId: string; uniqueCode: number; isCODVal: boolean; isSimpleEmbed: boolean;
    herbalClaimMode: any; discountAmount: number; voucherCode: string;
    bumpProductIds?: string[]; bumpSum?: number;
  };
  rpcOpts: { isSimpleEmbed: boolean; isSeamlessEmbed: boolean };
  orderMeta: { form: any; identifier: string; navigate: any };
  setters: {
    setIsSubmitting: (v: boolean) => void;
    setSubmission: (v: any) => void;
    hasSubmittedRef: { current: boolean };
    submitLockRef: { current: boolean };
  };
  mapValidationError: (message?: string) => string | null;
}

// Generate orderId ONCE di luar retry loop — mencegah duplicate order.
// Duplikat pesanan aktif (PESANAN_DUPLIKAT) → pesan ramah, JANGAN retry.
// Duplicate key → first attempt sukses tapi response hilang → verifikasi.
// Error validasi RPC deterministik → pesan ramah, JANGAN retry (hemat ~14 detik).
export async function runSubmitWithRetry(args: SubmitRetryArgs): Promise<void> {
  const { s, c, builtParams, rpcOpts, orderMeta, setters, mapValidationError } = args;
  const { setIsSubmitting, setSubmission, hasSubmittedRef, submitLockRef } = setters;
  const { form, identifier, navigate } = orderMeta;
  const { orderId } = builtParams;

  let retryCount = 0;
  const maxRetries = 3;
  let lastError: any = null;
  let lastBuiltPhone: string | undefined;

  while (retryCount <= maxRetries) {
    if (hasSubmittedRef.current) { setIsSubmitting(false); submitLockRef.current = false; return; }
    try {
      const built = await buildOrderPayload(builtParams);
      lastBuiltPhone = built.phone;

      await submitPublicOrder({ s, c, built, isSimpleEmbed: rpcOpts.isSimpleEmbed, isSeamlessEmbed: rpcOpts.isSeamlessEmbed });

      setIsSubmitting(false);
      submitLockRef.current = false;
      return;
    } catch (error: any) {
      // Duplikat pesanan aktif (RPC submit_public_order menolak) — tampilkan
      // pesan ramah, JANGAN retry. TIDAK menandai hasSubmittedRef karena order
      // belum tentu berhasil dibuat — customer masih boleh coba lagi.
      if (error?.message && error.message.includes('PESANAN_DUPLIKAT')) {
        setSubmission({ success: false, error: 'Kamu sudah punya pesanan produk ini yang sedang diproses. Kalau mau dibantu, hubungi admin ya.' });
        setIsSubmitting(false);
        submitLockRef.current = false;
        return;
      }
      // Jika duplicate key — first attempt sukses tapi response hilang
      const isDuplicate = error?.code === '23505' ||
        (error?.message && (error.message.includes('duplicate key') || error.message.includes('unique constraint')));
      if (isDuplicate) {
        await handleDuplicateKey({ formId: form?.id, orderId, identifier, phone: lastBuiltPhone, navigate, hasSubmittedRef, setIsSubmitting, submitLockRef });
        return;
      }

      const friendlyValidationMsg = mapValidationError(error?.message);
      if (friendlyValidationMsg) {
        setSubmission({ success: false, error: friendlyValidationMsg });
        setIsSubmitting(false);
        submitLockRef.current = false;
        return;
      }

      lastError = error;
      retryCount++;
      if (retryCount <= maxRetries) {
        setSubmission({ success: false, error: `Mencoba ulang... (${retryCount}/${maxRetries})`, retrying: true });
        await new Promise(r => setTimeout(r, Math.pow(2, retryCount) * 1000));
      }
    }
  }

  setIsSubmitting(false);
  submitLockRef.current = false;
  logError(lastError instanceof Error ? lastError : 'Gagal membuat pesanan setelah retry', 'form_submit_order', { identifier, formId: form?.id });
  setSubmission({ success: false, error: lastError?.message || 'Gagal membuat pesanan. Silakan coba lagi.' });
}
